/* eslint-disable react-hooks/rules-of-hooks */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import ApiService from "./../services/AxiosInstance";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { selectAuthToken } from "./../store/selectors/AuthSelectors";

export const useDataFetch = () => {
  const queryClient = useQueryClient();
  const token = useSelector(selectAuthToken);

  const getData = (
    queryKey,
    url,
    options = {},
    params = {},
    headers = {},
    axiosOptions = {}
  ) => {
    if (token) {
      headers = { ...headers, Authorization: `Bearer ${token}` };
    }
    return useQuery({
      queryKey,
      queryFn: async () => {
        const response = await ApiService.get(url, {
          ...axiosOptions,
          headers,
          params,
        });
        return response.data;
      },
      retry: 2,
      enabled: false,
      refetchOnWindowFocus: false,

      onError: (error) => {
        console.error("Query Error", error);
      },
      ...options,
    });
  };

  const postData = (url, headers = {}, extraBody = {}, options = {}) => {
    if (token) {
      headers = { ...headers, Authorization: `Bearer ${token}` };
    }
    return useMutation({
      mutationFn: async (body = {}) =>
        ApiService.post(url, { ...body, ...extraBody }, { headers }),
      onSuccess: () => {
        toast.success("Se completó la acción correctamente.");
      },
      onError: (error) => {
        console.log(error);
      },
      ...options,
    });
  };

  const postDataWithParam = (
    url,
    headers = {},
    extraBody = {},
    options = {}
  ) => {
    if (token) {
      headers = { ...headers, Authorization: `Bearer ${token}` };
    }
    return useMutation({
      mutationFn: async (body = {}) =>
        ApiService.post(
          `${url}${body.id ? "/" + body.id : ""}`,
          { ...body, ...extraBody },
          { headers }
        ),
      onSuccess: () => {
        toast.success("Se completó la acción correctamente.");
      },
      onError: (error) => {
        console.log(error);
      },
      ...options,
    });
  };

  const postMultipart = (url, headers = {}, options = {}) => {
    if (token) {
      headers = { ...headers, Authorization: `Bearer ${token}` };
    }
    return useMutation({
      mutationFn: async (formData) =>
        ApiService.post(url, formData, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        }),
      onSuccess: () => {
        toast.success("Se completó la acción correctamente.");
      },
      onError: (error) => {
        console.error("Error en multipart:", error);
      },
      ...options,
    });
  };

  const deleteData = (url, headers = {}, options = {}) => {
    if (token) {
      headers = { ...headers, Authorization: `Bearer ${token}` };
    }
    return useMutation({
      mutationFn: async (body = {}) =>
        Swal.fire({
          title: "Confirmación",
          text: "¿Desea eliminar el registro?",
          icon: "question",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Sí",
        }).then((result) => {
          if (result.isConfirmed) {
            return ApiService.delete(`${url}${body.id ? "/" + body.id : ""}`, {
              headers,
            });
          }
        }),
      onSuccess: () => {
        toast.success("Se completó la acción correctamente.");
      },
      onError: (error) => {
        console.log(error);
      },
      ...options,
    });
  };

  const patchData = (url, headers = {}, options = {}) => {
    if (token) {
      headers = { ...headers, Authorization: `Bearer ${token}` };
    }
    return useMutation({
      mutationFn: async (body = {}) =>
        ApiService.put(
          `${url}${body.id ? "/" + body.id : ""}`,
          { ...body },
          { headers }
        ),
      onSuccess: () => {
        toast.success("Se completó la acción correctamente.");
      },
      onError: (error) => {
        console.log(error);
      },
      ...options,
    });
  };

  const invalidateQuery = (queryKey) => {
    queryClient.invalidateQueries({ queryKey });
  };

  return {
    getData,
    postData,
    postDataWithParam,
    postMultipart,
    deleteData,
    patchData,
    invalidateQuery,
  };
};
